import styles from './Authorization.module.scss';

import { PasswordInput, Text, Group, TextInput, Button, Alert } from '@mantine/core';
import { ChangeEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_AUTH_LOGIN_ENDPOINT } from '@/api/config';
import { saveToken, getToken } from '@/utils/cookies';
import { useLanguage, type Language } from '@/i18n/LanguageContext';

function Login() {
  const { setLanguageFromServer, t } = useLanguage();
  const navigate = useNavigate();
  const [ state, setState ] = useState({
    name: '',
    password: '',
  });
  const [ error, setError ] = useState<string | null>(null);
  const [ loading, setLoading ] = useState(false);

  const onNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setState({
      ...state,
      name: event.target.value,
    });
    setError(null);
  };

  const onPasswordChange = (event: ChangeEvent<HTMLInputElement>) => {
    setState({
      ...state,
      password: event.target.value,
    });
    setError(null);
  };

  const onSignInButtonClick = async () => {
    if (!state.name || !state.password) {
      setError(t('login.missingCredentials'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post<{
        access_token: string;
        token_type: string;
        company_name: string;
        language?: Language;
        role?: 'admin' | 'operator';
        operator_id?: number | null;
        operator_name?: string | null;
      }>(
        API_AUTH_LOGIN_ENDPOINT,
        {
          username: state.name,
          password: state.password,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.access_token) {
        // Save token to cookie
        saveToken(response.data.access_token);
        if (response.data.language === 'en' || response.data.language === 'es') {
          setLanguageFromServer(response.data.language);
        }
        
        // Save username to localStorage for backward compatibility
        localStorage.setItem('DemoLogin', state.name);
        
        // Save company_name to localStorage for display in Header
        if (response.data.company_name) {
          localStorage.setItem('company_name', response.data.company_name);
        }
        localStorage.setItem('user_role', response.data.role || 'admin');
        if (response.data.operator_name) {
          localStorage.setItem('operator_name', response.data.operator_name);
        } else {
          localStorage.removeItem('operator_name');
        }
        
        // Force page reload to update Header and other components
        window.location.href = response.data.role === 'operator'
          ? '/rig_well'
          : '/';
      } else {
        setError(t('login.invalidResponse'));
      }
    } catch (error) {
      console.error('Login error:', error);
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        setError(t('login.invalidCredentials'));
      } else if (
        axios.isAxiosError(error) &&
        typeof error.response?.data?.detail === 'string'
      ) {
        setError(error.response.data.detail);
      } else {
        setError(t('login.failed'));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = getToken();
    if (token) {
      navigate('/');
      return;
    }
  }, [navigate]);

  return (
    <>
      <div className={styles.loginPage}>
        <div className={styles.loginShell}>
          <section className={styles.welcomePanel}>
            <p>{t('login.about')}</p>
          </section>

          <div className={styles.loginContainer}>
            <h2>{t('login.title')}</h2>
            <span className={styles.subtitle}>{t('login.subtitle')}</span>
            <div className={styles.nameInput}>
              <Group mb={5}>
                <Text component='label' htmlFor='name-input' size='sm'>
                  {t('login.name')}
                </Text>
              </Group>
              <TextInput
                placeholder={t('login.name')}
                id='name-input'
                value={state.name}
                onChange={onNameChange}
                size="sm"
              />
            </div>
            <div className={styles.passwordInput}>
              <Group mb={5}>
                <Text component='label' htmlFor='password-input' size='sm'>
                  {t('login.password')}
                </Text>
              </Group>
              <PasswordInput
                placeholder={t('login.password')}
                id='password-input'
                value={state.password}
                onChange={onPasswordChange}
                size="sm"
              />
            </div>
            <div className={styles.signInRow}>
              <Button color="green" onClick={onSignInButtonClick} loading={loading}>
                {t('login.signIn')}
              </Button>
            </div>
            {error && (
              <div className={styles.errorRow}>
                <Alert color="red">
                  {error}
                </Alert>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default Login;
